import styled from '@emotion/styled';
import {
  Paper,
  RootRef,
  Table as _Table,
  TableBody,
  TableHead,
  TableRow as _TableRow,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { createElement, FC, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import { CreativeForPeriodicImprovementPerformanceWithAllFieldsFragment } from '../../../../generated/graphql';
import useScrollableBoxShadow, { BoxShadowProps } from '../../../../hooks/useScrollableBoxShadow';
import { ExtractedData } from '../../../../utility/extractPeriodicImprovementPerformance';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import { breakpoint } from '../../../styleUtility/mediaquery';
import CreativeCard from './CreativeCard';
import ImprovementRateTableCells, { CreativeCell, TableCell } from './ImprovementRateTableCells';

type Data = {
  actualValue: ExtractedData;
  improvementRate: {
    all: ExtractedData;
    video: ExtractedData;
    staticImage: ExtractedData;
  };
} & CreativeForPeriodicImprovementPerformanceWithAllFieldsFragment;

type Props = {
  data: Data[];
  className?: string;
};

const columns = [
  { id: 'value_type', labelId: 'improvement_rate_table.table_head.value_type' },
  { id: 'target', labelId: 'improvement_rate_table.table_head.target' },
  { id: 'impressions', labelId: 'performance_report_metric.impressions' },
  { id: 'click_through_rate', labelId: 'performance_report_metric.click_through_rate' },
  { id: 'cost_per_click', labelId: 'performance_report_metric.cost_per_click' },
  { id: 'conversion_rate', labelId: 'performance_report_metric.conversion_rate' },
  { id: 'cost_per_action', labelId: 'performance_report_metric.cost_per_action' },
  { id: 'view_through_cost_per_action', labelId: 'performance_report_metric.view_through_cost_per_action' },
  {
    id: 'video_view_rate_p25',
    labelId: 'performance_report_metric.video_view_rate_p25',
  },
  {
    id: 'video_view_rate_p95',
    labelId: 'performance_report_metric.video_view_rate_p95',
  },
  {
    id: 'video_view_rate_p100',
    labelId: 'performance_report_metric.video_view_rate_p100',
  },
];

const ImprovementRateTable: FC<Props> = ({ data, className }) => {
  const { tableRef, scrollContentRef, headerRef, scrolling, handleScroll, boxShadowSize } = useScrollableBoxShadow();
  const isSmartPhoneScreenWidth = useMediaQuery(`(max-width:${breakpoint.sp}px)`);

  const getTableCellAlign = (columnName: string) => {
    switch (columnName) {
      case 'creative':
        return 'center';
      case 'target':
      case 'value_type':
        return 'left';
      default:
        return 'right';
    }
  };

  const hasKeyRatings = data.some((creative) => creative.improvementRate.all.video_view_rate_p95 !== undefined);
  const noKeyRatings = data.every(
    (creative) =>
      creative.improvementRate.all.video_view_rate_p95 === undefined &&
      creative.improvementRate.all.video_view_rate_p100 === undefined,
  );
  const columnList =
    hasKeyRatings || noKeyRatings
      ? columns.filter((column) => column.id !== 'video_view_rate_p100')
      : columns.filter((column) => column.id !== 'video_view_rate_p95');

  return (
    <Root className={className}>
      {scrolling && !isSmartPhoneScreenWidth && <BoxShadow width={boxShadowSize.width} height={boxShadowSize.height} />}

      <TableWrapper ref={tableRef} onScroll={handleScroll}>
        <Table>
          <TableHead>
            <TableRow>
              <RootRef rootRef={headerRef}>
                <CreativeCell align={getTableCellAlign('creative')}>
                  <Typography variant="caption" noWrap>
                    <FormattedMessage id={'improvement_rate_table.table_head.creative'} />
                  </Typography>
                </CreativeCell>
              </RootRef>

              {columnList.map((column) => (
                <TableCell align={getTableCellAlign(column.id)} key={column.id}>
                  <Typography variant="caption" noWrap>
                    <FormattedMessage id={column.labelId} />
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <RootRef rootRef={scrollContentRef}>
            <TableBody>
              {data.map((creative) => (
                <Fragment key={creative.creativeId}>
                  <TableRow>
                    <CreativeCell rowSpan={4}>
                      <CreativeCard
                        thumbnailImageUrl={creative.thumbnailImageUrl}
                        creation={creative.creation || null}
                      />
                    </CreativeCell>
                    <TableCell align="left">
                      <Typography noWrap>
                        <FormattedMessage id="periodic_improvement_performance.performance" />
                      </Typography>
                    </TableCell>
                    <ImprovementRateTableCells data={creative.actualValue} hasKeyRatings={hasKeyRatings} />
                  </TableRow>

                  <TableRow>
                    <TableCell align="left" rowSpan={3}>
                      <Typography noWrap>
                        <FormattedMessage id="periodic_improvement_performance.improvement_rate" />
                      </Typography>
                    </TableCell>
                    <ImprovementRateTableCells
                      data={creative.improvementRate.all}
                      hasKeyRatings={hasKeyRatings}
                      isImprovementRate
                    />
                  </TableRow>

                  <TableRow>
                    <ImprovementRateTableCells
                      data={creative.improvementRate.video}
                      hasKeyRatings={hasKeyRatings}
                      isImprovementRate
                    />
                  </TableRow>

                  <TableRow>
                    <ImprovementRateTableCells
                      data={creative.improvementRate.staticImage}
                      hasKeyRatings={hasKeyRatings}
                      isImprovementRate
                    />
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </RootRef>
        </Table>
      </TableWrapper>
    </Root>
  );
};

const Root = styled(Paper)({
  ...gridLayoutContainer({
    areas: [['improvement-table']],
    columns: ['auto'],
    rows: ['auto'],
    alignItems: 'center',
  }),
  position: 'relative',
  overflow: 'auto',
});

const BoxShadow = styled('div')<BoxShadowProps>((props: BoxShadowProps) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  width: props.width,
  height: props.height,
  zIndex: 10,
  boxShadow: '3px -0px 5px -2px rgba(0,0,0,0.1)',
}));

const TableWrapper = styled('div')({
  ...gridLayoutItem('improvement-table'),
  overflowX: 'auto',
  position: 'relative',
  zIndex: 1,
});

const Table = styled(_Table)({
  borderCollapse: 'separate',
});

const TableRow = styled(_TableRow)({
  height: 40,
});

export default ImprovementRateTable;
